import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, TextField, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, MenuItem,
  Grid, IconButton, Autocomplete, Typography, Chip,
  Menu, Divider, ListItemIcon, ListItemText, Tooltip,
} from '@mui/material';
import { Add, Send, Warning, Place, FiberManualRecord, NoteAdd, QuestionAnswer, Clear } from '@mui/icons-material';
import CaseEntryPanel from '../../components/CaseEntryPanel';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../api/client';
import { escapeHtml, safeColor, safeUrl, jsonForScript } from '../../utils/html';
import useCADWebSocket, { SoundConfig } from '../../hooks/useCADWebSocket';

interface CallType { id: number; code: string; name: string; priority: number; }
interface StatusType { id: number; code: string; name: string; color: string; }
interface Agency { id: number; name: string; }
interface CallPriorityOption { id: number; code: number; name: string; color: string; sort_order: number; }
interface LocationResult {
  id: number; name: string; category: string; address: string; apt_suite: string;
  city: string; state: string; zip_code: string; latitude: number | null;
  longitude: number | null; hazard_info: string; additional_notes: string;
}

export default function MapPage() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const [command, setCommand] = useState('');
  const [commandLog, setCommandLog] = useState<string[]>(['System ready. Type /help for commands.']);
  const commandLogRef = useRef<HTMLDivElement>(null);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [newCallOpen, setNewCallOpen] = useState(false);
  const [caseEntryOpen, setCaseEntryOpen] = useState(false);
  const [createdCall, setCreatedCall] = useState<any>(null);
  const [newNote, setNewNote] = useState('');
  const [callTypes, setCallTypes] = useState<CallType[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [callPriorities, setCallPriorities] = useState<CallPriorityOption[]>([]);
  const [callForm, setCallForm] = useState({
    call_type_id: 0, priority: 3, location_address: '', location_lat: null as number | null,
    location_lng: null as number | null, location_apt: '', cross_street: '',
    reporting_party: '', reporting_phone: '', narrative: '', agency_id: null as number | null,
  });
  const [locationResults, setLocationResults] = useState<LocationResult[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationResult | null>(null);
  const locationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unitMarkersRef = useRef<Record<number, L.Marker>>({});
  const callMarkersRef = useRef<Record<number, L.Marker>>({});
  const [previewCoords, setPreviewCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewAreaInfo, setPreviewAreaInfo] = useState('');
  const previewMapRef = useRef<HTMLDivElement | null>(null);
  const previewMapInstance = useRef<L.Map | null>(null);
  const previewMarkerRef = useRef<L.Marker | null>(null);
  const [statusTypes, setStatusTypes] = useState<StatusType[]>([]);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const [ctxUnit, setCtxUnit] = useState<any>(null);
  const cameraMarkersRef = useRef<L.Marker[]>([]);
  const cameraGroupRef = useRef<L.LayerGroup | null>(null);
  const radarLayerRef = useRef<L.TileLayer | null>(null);
  const trafficGroupRef = useRef<L.LayerGroup | null>(null);
  const trafficEnabledRef = useRef(false);
  const warningsGroupRef = useRef<L.LayerGroup | null>(null);
  const warningsEnabledRef = useRef(false);

  const buildCamIcon = (zoom?: number) => {
    const z = zoom ?? mapInstance.current?.getZoom() ?? 13;
    const scale = Math.max(0.5, Math.min(1.5, (z - 6) / 8));
    const size = Math.round(20 * scale);
    const svgSize = Math.round(12 * scale);
    return L.divIcon({
      className: '',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
      html: `<div style="width:${size}px;height:${size}px;background:#1565c0;border-radius:3px;border:1px solid #fff;display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 1px 3px rgba(0,0,0,0.4);">
        <svg viewBox="0 0 24 24" width="${svgSize}" height="${svgSize}"><path d="M17 10.5V7c0-.6-.4-1-1-1H4c-.6 0-1 .4-1 1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1v-3.5l4 4v-11l-4 4z" fill="white"/></svg>
      </div>`,
    });
  };

  const getCallTypeSvg = (code: string): string => {
    const c = code.toUpperCase();
    // Fire-related
    if (c.includes('FIRE') || c.includes('BLAZE') || c.includes('BURN') || c.includes('ARSON') || c === 'FIR')
      return '<path d="M12 23c-3.9 0-7-2.9-7-6.5 0-2.5 1.5-4.8 3-6.5l1.5 1.5c-1.3 1.4-2.5 3.2-2.5 5 0 2.8 2.2 5 5 5s5-2.2 5-5c0-1-.3-1.9-.8-2.8l1.3-1.3c.8 1.2 1.5 2.6 1.5 4.1 0 3.6-3.1 6.5-7 6.5zm1-14.7L10.3 5 12 2c0 0 4 3.5 4 7.5 0 2.5-2 4.5-4.5 4.5S7 12 7 9.5c0-.8.2-1.5.5-2.2l1.5 1.5c0 .2-.1.5-.1.7 0 1.4 1.1 2.5 2.5 2.5s2.5-1.1 2.5-2.5c0-1.7-1-3.2-1.9-4.2z" fill="white"/>';
    // Medical / EMS
    if (c.includes('MED') || c.includes('EMS') || c.includes('AMBULANCE') || c.includes('RESCUE') || c.includes('CARDIAC') || c.includes('INJURY') || c.includes('SICK'))
      return '<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 11h-4v4h-4v-4H6v-4h4V6h4v4h4v4z" fill="white"/>';
    // Law enforcement / Police
    if (c.includes('LAW') || c.includes('POLICE') || c.includes('PD') || c.includes('THEFT') || c.includes('BURGLARY') || c.includes('ROBBERY') || c.includes('ASSAULT') || c.includes('CRIME') || c.includes('WARRANT') || c.includes('ARREST') || c.includes('SUSPICIOUS') || c.includes('TRESPASS'))
      return '<path d="M12 1L3 5v6c0 5.6 3.8 10.7 9 12 5.2-1.3 9-6.4 9-12V5l-9-4zm0 11h7c-.5 4-3.1 7.6-7 8.8V12H5V6.3l7-3.1v8.8z" fill="white"/>';
    // Traffic / Accident / Vehicle
    if (c.includes('TRAFFIC') || c.includes('ACCIDENT') || c.includes('MVA') || c.includes('VEHICLE') || c.includes('DUI') || c.includes('CRASH') || c.includes('HIT'))
      return '<path d="M18.9 5.9l-1-2.3C17.6 2.6 16.6 2 15.5 2h-7c-1.1 0-2.1.6-2.4 1.6l-1 2.3C4.4 6.5 4 7.4 4 8.5V18c0 .6.4 1 1 1h1c.6 0 1-.4 1-1v-1h10v1c0 .6.4 1 1 1h1c.6 0 1-.4 1-1V8.5c0-1.1-.4-2-1.1-2.6zM7.5 15c-.8 0-1.5-.7-1.5-1.5S6.7 12 7.5 12s1.5.7 1.5 1.5S8.3 15 7.5 15zm9 0c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5 1.5.7 1.5 1.5-.7 1.5-1.5 1.5zM5.8 9l1.1-3.3c.1-.2.3-.4.6-.4h9c.3 0 .5.2.6.4L18.2 9H5.8z" fill="white"/>';
    // Alarm
    if (c.includes('ALARM'))
      return '<path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.1-1.6-5.6-4.5-6.3V4c0-.8-.7-1.5-1.5-1.5s-1.5.7-1.5 1.5v.7C7.6 5.4 6 7.9 6 11v5l-2 2v1h16v-1l-2-2z" fill="white"/>';
    // Hazmat / Chemical
    if (c.includes('HAZMAT') || c.includes('CHEMICAL') || c.includes('SPILL'))
      return '<path d="M14 2H10v2h1v4.3L7.2 14c-.5.8-.2 1.8.5 2.4.3.2.6.3 1 .3h6.6c.4 0 .7-.1 1-.3.7-.6 1-1.6.5-2.4L13 8.3V4h1V2zM12 15.5c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5 1.5.7 1.5 1.5-.7 1.5-1.5 1.5z" fill="white"/>';
    // Domestic / Disturbance
    if (c.includes('DOMESTIC') || c.includes('DISTURBANCE') || c.includes('DISPUTE') || c.includes('DISORDERLY') || c.includes('NOISE'))
      return '<path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="white"/>';
    // Missing person / Search
    if (c.includes('MISSING') || c.includes('SEARCH') || c.includes('LOST'))
      return '<path d="M12 12c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4z" fill="white"/>';
    // Default — generic call icon
    return '<path d="M20 15.5c-1.2 0-2.5-.2-3.6-.6-.4-.1-.8 0-1.1.3l-2.2 2.2c-2.8-1.4-5.1-3.8-6.6-6.6l2.2-2.2c.3-.3.4-.7.3-1.1-.4-1.1-.6-2.4-.6-3.6 0-.5-.5-1-1-1H4c-.5 0-1 .5-1 1 0 9.4 7.6 17 17 17 .5 0 1-.5 1-1v-3.5c0-.5-.5-1-1-1z" fill="white"/>';
  };

  const buildCallIcon = (cadNumber: string, priority: number, callTypeCode: string) => {
    const colors: Record<number, string> = { 1: '#f44336', 2: '#ff9800', 3: '#2196f3', 4: '#4caf50', 5: '#9e9e9e' };
    const color = colors[priority] || '#9e9e9e';
    const svgPath = getCallTypeSvg(callTypeCode);
    return L.divIcon({
      className: '',
      iconSize: [40, 48],
      iconAnchor: [20, 48],
      popupAnchor: [0, -48],
      html: `<div style="text-align:center;">
        <div style="width:32px;height:32px;border-radius:50%;background:${color};border:2px solid #fff;margin:0 auto;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.4);">
          <svg viewBox="0 0 24 24" width="20" height="20">${svgPath}</svg>
        </div>
        <div style="background:#333;color:#fff;font-size:9px;font-weight:700;padding:1px 3px;border-radius:3px;margin-top:2px;white-space:nowrap;display:inline-block;border:1px solid rgba(255,255,255,0.3);">${escapeHtml(cadNumber)}</div>
      </div>`,
    });
  };

  const buildUnitIcon = (u: { callsign: string; status_color: string; unit_type_icon: string }, zoom?: number) => {
    const color = safeColor(u.status_color);
    const z = zoom ?? mapInstance.current?.getZoom() ?? 13;
    const scale = Math.max(0.6, Math.min(2.5, (z - 6) / 6));
    const imgSize = Math.round(40 * scale);
    const fontSize = Math.round(12 * scale);
    const pad = Math.max(2, Math.round(4 * scale));
    const iconImg = u.unit_type_icon
      ? `<img src="${escapeHtml(u.unit_type_icon)}" style="width:${imgSize}px;height:${imgSize}px;object-fit:contain;display:block;margin:0 auto;" />`
      : `<div style="width:${imgSize}px;height:${imgSize}px;border-radius:50%;background:${color};border:2px solid #fff;margin:0 auto;"></div>`;
    return L.divIcon({
      className: '',
      iconSize: [0, 0],
      iconAnchor: [0, 0],
      popupAnchor: [0, -(imgSize + fontSize + 8)],
      html: `<div style="text-align:center;">
        ${iconImg}
        <div style="background:${color};color:#fff;font-size:${fontSize}px;font-weight:700;padding:1px ${pad}px;border-radius:3px;margin-top:2px;white-space:nowrap;display:inline-block;border:1px solid rgba(0,0,0,0.3);">${escapeHtml(u.callsign)}</div>
      </div>`,
    });
  };

  const geocodeSpecialAddress = async (addr: string): Promise<{ lat: number; lng: number } | null> => {
    const mmMatch = addr.match(/^(I[-\s]?\d+)\s+(?:at\s+)?(?:(?:MM|mile\s*(?:marker)?)\s*(\d+(?:\.\d+)?)|(\d+(?:\.\d+)?)\s*(?:MM|mile\s*(?:marker)?))/i);
    if (mmMatch) {
      const intNum = mmMatch[1].replace(/[^0-9]/g, '');
      const mm = mmMatch[2] || mmMatch[3];
      try {
        const r = await api.get('/geocode/milemarker', { params: { interstate: intNum, mile: mm } });
        if (r.data.lat && r.data.lng) return { lat: r.data.lat, lng: r.data.lng };
      } catch { /* fall through */ }
      return null;
    }
    const intMatch = addr.match(/^([^&\/]+?)\s*(?:&|and|\/)\s*([^&\/]+)$/i);
    if (intMatch && !addr.match(/^\d+\s/)) {
      const st1 = intMatch[1].trim();
      const st2 = intMatch[2].trim();
      try {
        const bbox = `${mapCenter.lat - 1},${mapCenter.lng - 1},${mapCenter.lat + 1},${mapCenter.lng + 1}`;
        const q = `[out:json][timeout:10];way["highway"]["name"~"${st1}",i](${bbox})->.a;way["highway"]["name"~"${st2}",i](${bbox})->.b;node(w.a)(w.b);out 1;`;
        const ovR = await fetch('https://overpass-api.de/api/interpreter', {
          method: 'POST', body: `data=${encodeURIComponent(q)}`,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
        const ovData = await ovR.json();
        if (ovData.elements?.length > 0) {
          return { lat: ovData.elements[0].lat, lng: ovData.elements[0].lon };
        }
      } catch { /* fall through */ }
      try {
        const nq = encodeURIComponent(`${st1} and ${st2}, ${jurisdictionCounty}, ${jurisdictionState}`);
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${nq}&limit=1`);
        const d = await r.json();
        if (d.length > 0) return { lat: parseFloat(d[0].lat), lng: parseFloat(d[0].lon) };
      } catch { /* fall through */ }
      return null;
    }
    return null;
  };

  const geocodeCoords = async (lat: number, lng: number) => {
    setPreviewCoords({ lat, lng });
    try {
      const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`);
      const revData = await revR.json();
      const addr = revData?.address || {};
      const parts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
      setPreviewAreaInfo(parts.join(', '));
      const mainRoad = (addr.road || '').toLowerCase();
      const radius = 100;
      const query = `[out:json][timeout:5];way["highway"]["name"](around:${radius},${lat},${lng});out tags;`;
      const ovR = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: `data=${encodeURIComponent(query)}`,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const ovData = await ovR.json();
      const allNames: string[] = (ovData.elements || [])
        .map((e: any) => e.tags?.name)
        .filter((n: string | undefined) => n && n.toLowerCase() !== mainRoad);
      const streets = allNames.filter((n: string, i: number) => allNames.indexOf(n) === i);
      if (streets.length > 0) {
        setCallForm((f) => ({ ...f, cross_street: f.cross_street || streets.slice(0, 2).join(' / ') }));
      }
    } catch { /* ignore */ }
  };

  const nominatimSearch = async (query: string, vb?: string): Promise<any[]> => {
    const params = new URLSearchParams({ format: 'json', addressdetails: '1', q: query, limit: '5' });
    if (vb) { params.set('viewbox', vb); params.set('bounded', '0'); }
    const r = await fetch(`https://nominatim.openstreetmap.org/search?${params}`);
    return r.json();
  };

  const pickBestResult = (results: any[], centerLat: number, centerLng: number) => {
    if (results.length === 0) return null;
    if (results.length === 1) return results[0];
    let best = results[0];
    let bestDist = Math.hypot(parseFloat(best.lat) - centerLat, parseFloat(best.lon) - centerLng);
    for (let i = 1; i < results.length; i++) {
      const d = Math.hypot(parseFloat(results[i].lat) - centerLat, parseFloat(results[i].lon) - centerLng);
      if (d < bestDist) { best = results[i]; bestDist = d; }
    }
    return best;
  };

  const handlePreviewAddress = async () => {
    if (!callForm.location_address) return;
    if (callForm.location_lat && callForm.location_lng) {
      setPreviewCoords({ lat: callForm.location_lat, lng: callForm.location_lng });
      try {
        const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${callForm.location_lat}&lon=${callForm.location_lng}&zoom=17&addressdetails=1`);
        const revData = await revR.json();
        const addr = revData?.address || {};
        const parts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
        setPreviewAreaInfo(parts.join(', '));
      } catch { /* ignore */ }
      return;
    }
    setPreviewLoading(true);
    try {
      const specialResult = await geocodeSpecialAddress(callForm.location_address);
      if (specialResult) {
        setCallForm((f) => ({ ...f, location_lat: specialResult.lat, location_lng: specialResult.lng }));
        setPreviewCoords(specialResult);
        try {
          const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${specialResult.lat}&lon=${specialResult.lng}&zoom=17&addressdetails=1`);
          const revData = await revR.json();
          const addr = revData?.address || {};
          const areaParts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
          setPreviewAreaInfo(areaParts.join(', '));
          if (!callForm.cross_street && addr.road) {
            setCallForm((f) => ({ ...f, cross_street: addr.road }));
          }
        } catch { /* ignore */ }
        setPreviewLoading(false);
        return;
      }
    } catch { /* fall through to standard geocode */ }
    // Multi-strategy geocoding with viewbox bias
    const vb = `${mapCenter.lng - 1},${mapCenter.lat - 1},${mapCenter.lng + 1},${mapCenter.lat + 1}`;
    let result: any = null;
    try {
      const q1 = [callForm.location_address, jurisdictionCounty, jurisdictionState].filter(Boolean).join(', ');
      const d1 = await nominatimSearch(q1, vb);
      result = pickBestResult(d1, mapCenter.lat, mapCenter.lng);
      if (!result && jurisdictionState) {
        const q2 = `${callForm.location_address}, ${jurisdictionState}`;
        const d2 = await nominatimSearch(q2, vb);
        result = pickBestResult(d2, mapCenter.lat, mapCenter.lng);
      }
      if (!result) {
        const d3 = await nominatimSearch(callForm.location_address, vb);
        result = pickBestResult(d3, mapCenter.lat, mapCenter.lng);
      }
      if (!result) {
        const noNum = callForm.location_address.replace(/^\d+\s+/, '');
        if (noNum !== callForm.location_address) {
          const q4 = [noNum, jurisdictionCounty, jurisdictionState].filter(Boolean).join(', ');
          const d4 = await nominatimSearch(q4, vb);
          result = pickBestResult(d4, mapCenter.lat, mapCenter.lng);
        }
      }
    } catch { /* ignore */ }
    if (result) {
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);
      setCallForm((f) => ({ ...f, location_lat: lat, location_lng: lng }));
      setPreviewCoords({ lat, lng });
      const addr = result.address || {};
      const areaParts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
      setPreviewAreaInfo(areaParts.join(', '));
      if (!callForm.cross_street) {
        try {
          const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`);
          const revData = await revR.json();
          const mainRoad = (revData?.address?.road || '').toLowerCase();
          const radius = 100;
          const query = `[out:json][timeout:5];way["highway"]["name"](around:${radius},${lat},${lng});out tags;`;
          const ovR = await fetch('https://overpass-api.de/api/interpreter', {
            method: 'POST',
            body: `data=${encodeURIComponent(query)}`,
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          });
          const ovData = await ovR.json();
          const allNames: string[] = (ovData.elements || [])
            .map((e: any) => e.tags?.name)
            .filter((n: string | undefined) => n && n.toLowerCase() !== mainRoad);
          const streets = allNames.filter((n: string, i: number) => allNames.indexOf(n) === i);
          if (streets.length > 0) {
            setCallForm((f) => ({ ...f, cross_street: streets.slice(0, 2).join(' / ') }));
          }
        } catch { /* cross street lookup failed, not critical */ }
      }
    } else {
      setPreviewCoords({ lat: mapCenter.lat, lng: mapCenter.lng });
      setPreviewAreaInfo('Address not found — drag pin to set location');
    }
    setPreviewLoading(false);
  };

  const resetPreviewMap = () => {
    setPreviewCoords(null);
    setPreviewAreaInfo('');
    if (previewMapInstance.current) {
      previewMapInstance.current.remove();
      previewMapInstance.current = null;
      previewMarkerRef.current = null;
    }
  };

  const resetNewCallForm = () => {
    setCallForm({
      call_type_id: 0, priority: 3, location_address: '', location_lat: null,
      location_lng: null, location_apt: '', cross_street: '',
      reporting_party: '', reporting_phone: '', narrative: '', agency_id: null,
    });
    resetPreviewMap();
  };

  // Initialize/update preview map when coords change
  useEffect(() => {
    if (!previewCoords || !previewMapRef.current) return;
    if (previewMapInstance.current) {
      previewMapInstance.current.setView([previewCoords.lat, previewCoords.lng], 16);
      previewMarkerRef.current?.setLatLng([previewCoords.lat, previewCoords.lng]);
      return;
    }
    const map = L.map(previewMapRef.current).setView([previewCoords.lat, previewCoords.lng], 16);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OSM' }).addTo(map);
    const pinIcon = L.divIcon({
      className: '',
      iconSize: [24, 24],
      iconAnchor: [12, 24],
      html: '<div style="width:24px;height:24px;background:#2196f3;border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>',
    });
    const marker = L.marker([previewCoords.lat, previewCoords.lng], { icon: pinIcon, draggable: true })
      .addTo(map);
    marker.on('dragend', async () => {
      const pos = marker.getLatLng();
      const lat = Math.round(pos.lat * 1e7) / 1e7;
      const lng = Math.round(pos.lng * 1e7) / 1e7;
      setCallForm((f) => ({ ...f, location_lat: lat, location_lng: lng }));
      try {
        const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`);
        const revData = await revR.json();
        const addr = revData?.address || {};
        const parts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
        setPreviewAreaInfo(parts.join(', '));
      } catch { /* ignore */ }
    });
    previewMarkerRef.current = marker;
    previewMapInstance.current = map;
  }, [previewCoords]);

  const loadUnitPositions = useCallback(() => {
    if (!mapInstance.current) return;
    api.get('/units').then((r) => {
      const map = mapInstance.current!;
      const activeUnitIds = new Set<number>();
      r.data.forEach((u: any) => {
        if (u.is_logged_on && u.location_lat && u.location_lng) {
          activeUnitIds.add(u.id);
          const icon = buildUnitIcon(u);
          const popup = `<b>${escapeHtml(u.callsign)}</b><br/>${escapeHtml(u.status_name || 'N/A')}<br/>${escapeHtml(u.agency_name)}`;
          if (unitMarkersRef.current[u.id]) {
            unitMarkersRef.current[u.id].setLatLng([u.location_lat, u.location_lng]);
            unitMarkersRef.current[u.id].setIcon(icon);
            unitMarkersRef.current[u.id].setPopupContent(popup);
            (unitMarkersRef.current[u.id] as any)._unitData = u;
          } else {
            const marker = L.marker([u.location_lat, u.location_lng], { icon })
              .addTo(map)
              .bindPopup(popup);
            (marker as any)._unitData = u;
            marker.on('contextmenu', (e: any) => {
              e.originalEvent.preventDefault();
              setCtxUnit(u);
              setCtxMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY });
            });
            unitMarkersRef.current[u.id] = marker;
          }
        }
      });
      // Remove markers for units no longer logged on
      Object.keys(unitMarkersRef.current).forEach((idStr) => {
        const id = Number(idStr);
        if (!activeUnitIds.has(id)) {
          map.removeLayer(unitMarkersRef.current[id]);
          delete unitMarkersRef.current[id];
        }
      });
    }).catch(() => {});

    // Load call markers
    api.get('/calls/pending').then((r) => {
      const map = mapInstance.current!;
      const activeCallIds = new Set<number>();
      r.data.forEach((c: any) => {
        if (c.location_lat && c.location_lng) {
          activeCallIds.add(c.id);
          const icon = buildCallIcon(c.cad_number, c.priority, c.call_type_code || '');
          const popup = `<b>${escapeHtml(c.cad_number)}</b><br/>${escapeHtml(c.call_type_name)}<br/>P${escapeHtml(c.priority)}<br/>${escapeHtml(c.location_address)}`;
          if (callMarkersRef.current[c.id]) {
            callMarkersRef.current[c.id].setLatLng([c.location_lat, c.location_lng]);
            callMarkersRef.current[c.id].setIcon(icon);
            callMarkersRef.current[c.id].setPopupContent(popup);
          } else {
            const marker = L.marker([c.location_lat, c.location_lng], { icon })
              .addTo(map)
              .bindPopup(popup);
            callMarkersRef.current[c.id] = marker;
          }
        }
      });
      // Remove markers for calls no longer active
      Object.keys(callMarkersRef.current).forEach((idStr) => {
        const id = Number(idStr);
        if (!activeCallIds.has(id)) {
          map.removeLayer(callMarkersRef.current[id]);
          delete callMarkersRef.current[id];
        }
      });
    }).catch(() => {});
  }, []);

  const [soundConfig, setSoundConfig] = useState<SoundConfig>({});
  const [jurisdictionState, setJurisdictionState] = useState('');
  const [jurisdictionCounty, setJurisdictionCounty] = useState('');
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 37.2, lng: -93.3 });

  useEffect(() => {
    api.get('/system-config/public').then((r) => {
      setSoundConfig({
        sound_dispatch_new_call: r.data.sound_dispatch_new_call,
        sound_dispatch_status_change: r.data.sound_dispatch_status_change,
      });
      if (r.data.jurisdiction_state) setJurisdictionState(r.data.jurisdiction_state);
      if (r.data.jurisdiction_county) setJurisdictionCounty(r.data.jurisdiction_county);
      if (r.data.map_center_lat && r.data.map_center_lng) setMapCenter({ lat: r.data.map_center_lat, lng: r.data.map_center_lng });
    }).catch(() => {});
  }, []);

  useCADWebSocket(loadUnitPositions, 'dispatch', soundConfig);

  useEffect(() => {
    api.get('/call-types').then((r) => setCallTypes(r.data));
    api.get('/agencies').then((r) => setAgencies(r.data));
    api.get('/call-priorities').then((r) => setCallPriorities(r.data));
    api.get('/status-types').then((r) => setStatusTypes(r.data));
  }, []);

  useEffect(() => {
    if (mapRef.current && !mapInstance.current) {
      const map = L.map(mapRef.current, { maxZoom: 22 }).setView([39.8283, -98.5795], 5);
      const streets = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors', maxZoom: 22, maxNativeZoom: 19,
      });
      const dark = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; OpenStreetMap &copy; CARTO', maxZoom: 22, maxNativeZoom: 20,
      });
      const satellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '&copy; Esri', maxZoom: 22, maxNativeZoom: 19,
      });
      streets.addTo(map);

      // Weather radar overlay (NOAA MRMS - high resolution 1km x 1km)
      const radarLayer = (L.tileLayer as any).wms('https://mapservices.weather.noaa.gov/eventdriven/services/radar/radar_base_reflectivity/MapServer/WMSServer', {
        layers: '0',
        format: 'image/png',
        transparent: true,
        opacity: 0.6,
        attribution: 'Weather data &copy; NOAA MRMS',
      });
      radarLayerRef.current = radarLayer;

      // Traffic cameras overlay (LayerGroup, toggleable)
      const cameraGroup = L.layerGroup();
      cameraGroupRef.current = cameraGroup;

      // Traffic incidents overlay (MODOT)
      const trafficGroup = L.layerGroup();
      trafficGroupRef.current = trafficGroup;
      const incidentColors: Record<string, string> = {
        closed: '#d32f2f', 'expect delays': '#f57c00', 'possible delays': '#fbc02d', future: '#9e9e9e', active: '#e91e63',
      };
      const addIncidentsToGroup = (items: any[]) => {
        (items || []).forEach((inc: any) => {
          if (!inc.lat || !inc.lng) return;
          const color = incidentColors[inc.impact?.toLowerCase()] || '#ef6c00';
          const icon = L.divIcon({
            className: '',
            iconSize: [16, 16],
            iconAnchor: [8, 8],
            html: `<div style="width:16px;height:16px;background:${color};border-radius:3px;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;">
              <span style="color:#fff;font-size:10px;font-weight:700;">!</span></div>`,
          });
          const src = inc.source ? `<br/><small style="color:#888">${escapeHtml(inc.source)}</small>` : '';
          L.marker([inc.lat, inc.lng], { icon })
            .bindPopup(`<b>${escapeHtml(inc.category || 'Incident')}</b>${inc.impact ? ` — ${escapeHtml(inc.impact)}` : ''}<br/>${escapeHtml(inc.route)}${inc.description ? `<br/><small>${escapeHtml(inc.description)}</small>` : ''}${src}`)
            .addTo(trafficGroup);
        });
      };
      const loadTrafficIncidents = () => {
        if (!trafficEnabledRef.current || !mapInstance.current) return;
        trafficGroup.clearLayers();
        // Fetch from all three sources in parallel
        api.get('/geocode/traffic-incidents').then((r) => addIncidentsToGroup(r.data)).catch(() => {});
        api.get('/geocode/kcscout-incidents').then((r) => addIncidentsToGroup(r.data)).catch(() => {});
        api.get('/geocode/ozarks-incidents').then((r) => addIncidentsToGroup(r.data)).catch(() => {});
      };

      // Severe Weather Warnings overlay (Tornado=red, Severe T-Storm=yellow)
      const warningsGroup = L.layerGroup();
      warningsGroupRef.current = warningsGroup;
      const loadSevereWarnings = () => {
        if (!warningsEnabledRef.current || !mapInstance.current) return;
        api.get('/geocode/severe-weather-alerts').then((r) => {
          warningsGroup.clearLayers();
          (r.data || []).forEach((alert: any) => {
            if (!alert.geometry) return;
            const isTornado = alert.event === 'Tornado Warning';
            const color = isTornado ? '#ff0000' : '#ffff00';
            const fillColor = isTornado ? '#ff0000' : '#ffff00';
            const coords = alert.geometry.coordinates;
            if (!coords || !coords.length) return;
            const polyCoords: [number, number][][] = alert.geometry.type === 'Polygon'
              ? [coords[0].map((c: any) => [c[1], c[0]] as [number, number])]
              : coords.map((ring: any) => ring[0].map((c: any) => [c[1], c[0]] as [number, number]));
            const poly = L.polygon(polyCoords, {
              color, fillColor, fillOpacity: isTornado ? 0.35 : 0.25, weight: 3,
            });
            const descEscaped = escapeHtml(alert.description).replace(/\n/g, '<br/>');
            const popupHtml = `<b style="color:${isTornado ? '#d32f2f' : '#f57c00'}">${escapeHtml(alert.event)}</b><br/>${escapeHtml(alert.headline)}<br/><small>Expires: ${alert.expires ? escapeHtml(new Date(alert.expires).toLocaleTimeString()) : 'N/A'}</small>${alert.description ? `<br/><a href="#" class="wc-warning-more" style="color:#2196f3;font-weight:700;font-size:12px;">More Details &raquo;</a><div class="wc-warning-full" style="display:none;margin-top:6px;max-height:300px;overflow:auto;font-size:12px;line-height:1.4;white-space:pre-wrap;border-top:1px solid #ccc;padding-top:6px;">${descEscaped}</div>` : ''}`;
            const popup = L.popup({ maxWidth: 400, maxHeight: 450 }).setContent(popupHtml);
            poly.bindPopup(popup);
            poly.on('popupopen', () => {
              setTimeout(() => {
                const el = document.querySelector('.wc-warning-more');
                if (el) {
                  el.addEventListener('click', (ev) => {
                    ev.preventDefault();
                    const full = (el as HTMLElement).nextElementSibling as HTMLElement;
                    if (full) { full.style.display = full.style.display === 'none' ? 'block' : 'none'; (el as HTMLElement).textContent = full.style.display === 'none' ? 'More Details »' : '« Less'; }
                    popup.update();
                  });
                }
              }, 50);
            });
            poly.addTo(warningsGroup);
          });
        }).catch(() => {});
      };
      // Auto-refresh warnings every 60 seconds
      const warningsInterval = setInterval(() => { if (warningsEnabledRef.current) loadSevereWarnings(); }, 60000);

      map.on('overlayadd', (e: any) => {
        if (e.name === 'MODOT Incidents') { trafficEnabledRef.current = true; loadTrafficIncidents(); }
        if (e.name === 'Severe Warnings') { warningsEnabledRef.current = true; loadSevereWarnings(); }
        if (e.name === 'Traffic Cameras') { camerasEnabledRef.current = true; loadCamerasForBounds(); }
      });
      map.on('overlayremove', (e: any) => {
        if (e.name === 'MODOT Incidents') { trafficEnabledRef.current = false; trafficGroup.clearLayers(); }
        if (e.name === 'Severe Warnings') { warningsEnabledRef.current = false; warningsGroup.clearLayers(); }
        if (e.name === 'Traffic Cameras') { camerasEnabledRef.current = false; cameraGroup.clearLayers(); cameraMarkersRef.current = []; }
      });
      map.on('moveend', () => { if (camerasEnabledRef.current) loadCamerasForBounds(); });

      const layersControl = L.control.layers(
        { 'Street Map': streets, 'Dark Mode': dark, 'Satellite': satellite },
        { 'Weather Radar': radarLayer, 'Traffic Cameras': cameraGroup, 'MODOT Incidents': trafficGroup, 'Severe Warnings': warningsGroup },
        { position: 'topright', collapsed: false },
      ).addTo(map);

      // Agency-supplied custom basemaps (Admin → GIS → Basemaps).
      api.get('/gis/basemaps').then((r) => {
        (r.data || []).forEach((bm: any) => {
          const opts = {
            attribution: bm.attribution || '',
            minZoom: bm.min_zoom || 0,
            maxZoom: bm.max_zoom || 22,
            maxNativeZoom: bm.max_native_zoom || 19,
          };
          const layer = bm.kind === 'wms'
            ? (L.tileLayer as any).wms(bm.url_template, {
                ...opts, layers: bm.wms_layers, format: bm.wms_format || 'image/png', transparent: bm.wms_transparent,
              })
            : L.tileLayer(bm.url_template, opts);
          layersControl.addBaseLayer(layer, bm.name);
          if (bm.is_default) {
            map.removeLayer(streets);
            layer.addTo(map);
          }
        });
      }).catch(() => {});
      mapInstance.current = map;

      map.on('zoomend', () => {
        const z = map.getZoom();
        Object.values(unitMarkersRef.current).forEach((marker) => {
          const el = (marker as any)._icon;
          if (!el) return;
          const u = (marker as any)._unitData;
          if (u) marker.setIcon(buildUnitIcon(u, z));
        });
        const camIc = buildCamIcon(z);
        cameraMarkersRef.current.forEach((m) => m.setIcon(camIc));
      });

      // Camera loading: only load when layer is toggled ON, filter by map bounds
      const camerasEnabledRef = { current: false };
      const cameraDataCache: Record<string, any[]> = {};
      const addCamerasToGroup = (cams: any[]) => {
        const camIc = buildCamIcon(map.getZoom());
        const bounds = map.getBounds();
        (cams || []).forEach((cam: any) => {
          if (!cam.lat || !cam.lng) return;
          if (!bounds.contains([cam.lat, cam.lng])) return;
          const streamUrl = cam.stream_url || '';
          const snapshotUrl = safeUrl(cam.snapshot_url);
          const camName = cam.name || 'Traffic Camera';
          const src = cam.source ? ` (${escapeHtml(cam.source)})` : '';
          const offline = cam.offline ? ' <span style="color:#f44336">[Offline]</span>' : '';
          let popupContent = `<b>${escapeHtml(camName)}</b>${src}${offline}<br/>`;
          if (streamUrl && !cam.offline) {
            const camId = `cam_${String(cam.id || Math.random().toString(36).slice(2)).replace(/[^A-Za-z0-9_-]/g, '_')}`;
            popupContent += `<a href="#" id="${camId}" style="color:#1976d2;font-weight:600;">Open Camera Feed</a>`;
          } else if (snapshotUrl) {
            const needsProxy = snapshotUrl.includes('travelmidwest.com') || snapshotUrl.includes('511la.org');
            const imgSrc = needsProxy ? `/api/geocode/camera-image-proxy?url=${encodeURIComponent(snapshotUrl)}` : snapshotUrl;
            popupContent += `<img src="${escapeHtml(imgSrc)}" style="width:280px;max-height:200px;object-fit:contain;" onerror="this.style.display='none'"/><br/><a href="${escapeHtml(imgSrc)}" target="_blank" rel="noopener noreferrer">Full Image</a>`;
          } else if (cam.offline) {
            popupContent += `<span style="color:#999">Stream temporarily unavailable</span>`;
          }
          const marker = L.marker([cam.lat, cam.lng], { icon: camIc })
            .addTo(cameraGroup)
            .bindPopup(popupContent, { maxWidth: 320 });
          if (streamUrl && !cam.offline) {
            const openFeed = async () => {
              let resolvedUrl = streamUrl;
              if (streamUrl.includes('modot') || streamUrl.includes('traveler') || streamUrl.startsWith('rtsp://')) {
                try {
                  const res = await api.get('/geocode/camera-stream-url', { params: { url: streamUrl } });
                  if (res.data?.stream_url) resolvedUrl = res.data.stream_url;
                } catch (_) { /* use original */ }
              }
              if (!/^https?:\/\//i.test(resolvedUrl)) return;
              const urlJs = jsonForScript(resolvedUrl);
              const playerHtml = `<!DOCTYPE html><html><head><title>${escapeHtml(camName)}</title><script src="https://cdn.jsdelivr.net/npm/hls.js@latest"><\/script><style>*{margin:0;padding:0;overflow:hidden;background:#000}video{width:100vw;height:100vh;object-fit:contain}</style></head><body><video id="v" autoplay muted></video><script>var v=document.getElementById('v');if(Hls.isSupported()){var h=new Hls();h.loadSource(${urlJs});h.attachMedia(v)}else{v.src=${urlJs}}v.play().catch(function(){});<\/script></body></html>`;
              const w = window.open('', '_blank', 'width=800,height=600,resizable=yes');
              if (w) { w.document.write(playerHtml); w.document.close(); }
            };
            marker.on('popupopen', () => {
              const popup = marker.getPopup();
              if (popup) {
                const el = popup.getElement();
                const link = el?.querySelector('a[id^="cam_"]');
                if (link) link.addEventListener('click', (e: any) => { e.preventDefault(); openFeed(); });
              }
            });
          }
          cameraMarkersRef.current.push(marker);
        });
      };
      const cameraEndpoints = [
        '/geocode/traffic-cameras', '/geocode/kansas-cameras', '/geocode/kcscout-cameras',
        '/geocode/florida-cameras', '/geocode/iowa-cameras', '/geocode/illinois-cameras',
        '/geocode/louisiana-cameras', '/geocode/utah-cameras',
      ];
      let camerasLoading = false;
      const loadCamerasForBounds = async () => {
        if (camerasLoading) return;
        camerasLoading = true;
        cameraGroup.clearLayers();
        cameraMarkersRef.current = [];
        for (const ep of cameraEndpoints) {
          try {
            if (!cameraDataCache[ep]) {
              const res = await api.get(ep);
              cameraDataCache[ep] = res.data || [];
            }
            // Only add markers that are within current viewport
            addCamerasToGroup(cameraDataCache[ep]);
          } catch (_) { /* skip failed source */ }
        }
        camerasLoading = false;
      };

      api.get('/system-config/public').then((r) => {
        map.setView([r.data.map_center_lat, r.data.map_center_lng], r.data.map_default_zoom);
        // Cameras default to OFF — user toggles on via layer control
      }).catch(() => {});

      setTimeout(loadUnitPositions, 500);
    }
  }, [loadUnitPositions]);

  const addLog = useCallback((msg: string) => {
    setCommandLog((prev) => [...prev.slice(-100), msg]);
  }, []);

  useEffect(() => {
    const el = commandLogRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [commandLog]);

  // Helper: find a unit by flexible callsign matching (exact, starts-with, contains)
  const findUnit = (unitsList: any[], search: string) => {
    const s = search.toLowerCase();
    return unitsList.find((u: any) => u.callsign.toLowerCase() === s)
      || unitsList.find((u: any) => u.callsign.toLowerCase().startsWith(s))
      || unitsList.find((u: any) => u.callsign.toLowerCase().includes(s));
  };

  // Helper: find unit from args, trying progressively more words from the start
  const findUnitInArgs = (unitsList: any[], args: string[]) => {
    // Try full args as callsign, then progressively fewer words
    for (let i = args.length; i >= 1; i--) {
      const search = args.slice(0, i).join(' ');
      const unit = findUnit(unitsList, search);
      if (unit) return { unit, remaining: args.slice(i) };
    }
    return { unit: null, remaining: args };
  };

  const handleCommand = async () => {
    if (!command.trim()) return;
    setCommandHistory((prev) => [...prev, command.trim()]);
    setHistoryIndex(-1);
    addLog(`> ${command}`);
    const parts = command.trim().split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    if (cmd === '/help') {
      addLog('Commands: /help, /newcall, /status <unit> <status>, /logon <unit>, /logoff <unit>, /dispatch <unit> <cad#>, /clear <unit>, /locate <unit> <location>, /units, /pending, @<location>');
    } else if (cmd === '/newcall') {
      setNewCallOpen(true);
    } else if (cmd === '/units') {
      try {
        const res = await api.get('/units');
        res.data.forEach((u: any) => addLog(`  ${u.callsign} [${u.status_name || 'N/A'}] - ${u.agency_name}`));
      } catch { addLog('Error loading units'); }
    } else if (cmd === '/pending') {
      try {
        const res = await api.get('/calls/pending');
        if (res.data.length === 0) { addLog('  No pending calls'); }
        else { res.data.forEach((c: any) => addLog(`  ${c.cad_number} P${c.priority} ${c.call_type_code} - ${c.location_address}`)); }
      } catch { addLog('Error loading calls'); }
    } else if (cmd === '/status' && args.length >= 2) {
      try {
        const unitsRes = await api.get('/units');
        const { unit, remaining } = findUnitInArgs(unitsRes.data, args);
        if (!unit) { addLog(`Unit ${args.join(' ')} not found`); }
        else if (remaining.length === 0) { addLog('Usage: /status <unit> <status_code>'); }
        else {
          const statuses = await api.get('/status-types');
          const statusSearch = remaining[0].toLowerCase();
          const st = statuses.data.find((s: any) => s.code.toLowerCase() === statusSearch || s.name.toLowerCase() === statusSearch);
          if (!st) { addLog(`Status ${remaining[0]} not found. Use code (AVL, ENR, ONS, TRN, etc.)`); }
          else {
            await api.patch(`/units/${unit.id}/status/${st.id}`);
            addLog(`${unit.callsign} status changed to ${st.name}`);
          }
        }
      } catch { addLog('Error changing status'); }
    } else if (cmd === '/logon' && args.length >= 1) {
      try {
        const unitsRes = await api.get('/units');
        const unitSearch = args.join(' ');
        const unit = findUnit(unitsRes.data, unitSearch);
        if (!unit) { addLog(`Unit ${unitSearch} not found`); }
        else {
          await api.post(`/units/${unit.id}/logon`);
          addLog(`${unit.callsign} logged on (Available)`);
          loadUnitPositions();
        }
      } catch { addLog('Error logging on unit'); }
    } else if (cmd === '/logoff' && args.length >= 1) {
      try {
        const unitsRes = await api.get('/units');
        const unitSearch = args.join(' ');
        const unit = findUnit(unitsRes.data, unitSearch);
        if (!unit) { addLog(`Unit ${unitSearch} not found`); }
        else {
          await api.post(`/units/${unit.id}/logoff`);
          addLog(`${unit.callsign} logged off (Out of Service)`);
          loadUnitPositions();
        }
      } catch { addLog('Error logging off unit'); }
    } else if (cmd === '/dispatch' && args.length >= 2) {
      try {
        const unitsRes = await api.get('/units');
        const { unit, remaining } = findUnitInArgs(unitsRes.data, args);
        if (!unit) { addLog(`Unit ${args.join(' ')} not found`); }
        else if (remaining.length === 0) { addLog('Usage: /dispatch <unit> <cad#>'); }
        else {
          const callsRes = await api.get('/calls/pending');
          const searchTerm = remaining.join(' ').toLowerCase();
          const call = callsRes.data.find((c: any) =>
            c.cad_number.toLowerCase() === searchTerm ||
            c.cad_number.slice(-4).toLowerCase() === searchTerm.slice(-4).toLowerCase()
          );
          if (!call) { addLog(`Call ${remaining.join(' ')} not found`); }
          else {
            await api.post(`/units/${unit.id}/dispatch/${call.id}`);
            addLog(`${unit.callsign} dispatched to ${call.cad_number}`);
            loadUnitPositions();
          }
        }
      } catch { addLog('Error dispatching unit'); }
    } else if (cmd === '/clear' && args.length >= 1) {
      try {
        const unitsRes = await api.get('/units');
        const unitSearch = args.join(' ');
        const unit = findUnit(unitsRes.data, unitSearch);
        if (!unit) { addLog(`Unit ${unitSearch} not found`); }
        else if (!unit.assigned_call_id) { addLog(`${unit.callsign} is not assigned to a call`); }
        else {
          await api.post(`/units/${unit.id}/clear`);
          addLog(`${unit.callsign} cleared from call`);
          loadUnitPositions();
        }
      } catch { addLog('Error clearing unit'); }
    } else if (cmd === '/locate' && args.length >= 2) {
      try {
        const unitsRes = await api.get('/units');
        const { unit, remaining } = findUnitInArgs(unitsRes.data, args);
        if (!unit) { addLog(`Unit ${args.join(' ')} not found`); }
        else if (remaining.length === 0) { addLog('Usage: /locate <unit> <location>'); }
        else {
          const location = remaining.join(' ');
          const res = await api.post(`/units/${unit.id}/locate`, { location });
          addLog(`${unit.callsign} location set to: ${res.data.current_location || location}`);
          loadUnitPositions();
        }
      } catch { addLog('Error setting unit location'); }
    } else if (cmd.startsWith('@')) {
      const query = command.slice(1).trim();
      if (!query) { addLog('Usage: @<location name>'); }
      else {
        try {
          const res = await api.get(`/locations/search?q=${encodeURIComponent(query)}`);
          if (res.data.length === 0) { addLog(`No locations found for "${query}"`); }
          else {
            res.data.forEach((loc: LocationResult) => {
              let line = `  @${loc.name} — ${loc.address}`;
              if (loc.city) line += `, ${loc.city}`;
              if (loc.hazard_info) line += ` [HAZARD: ${loc.hazard_info}]`;
              if (loc.additional_notes) line += ` (${loc.additional_notes})`;
              addLog(line);
            });
          }
        } catch { addLog('Error searching locations'); }
      }
    } else {
      addLog(`Unknown command: ${cmd}. Type /help for help.`);
    }
    setCommand('');
  };

  // Transport destination state for map context menu
  const [mapTransportOpen, setMapTransportOpen] = useState(false);
  const [mapTransportDest, setMapTransportDest] = useState('');
  const [mapTransportUnit, setMapTransportUnit] = useState<any>(null);
  const [mapTransportStatusId, setMapTransportStatusId] = useState<number | null>(null);
  const [mapDestinations, setMapDestinations] = useState<{ name: string; address: string }[]>([]);

  const handleCtxStatusChange = async (statusId: number) => {
    if (!ctxUnit) return;
    // Check if Transporting status
    const st = statusTypes.find((s) => s.id === statusId);
    if (st && st.code === 'TRN') {
      setMapTransportUnit(ctxUnit);
      setMapTransportStatusId(statusId);
      setMapTransportDest('');
      api.get('/locations').then((r) => setMapDestinations(r.data.map((l: any) => ({ name: l.name, address: l.address }))));
      setMapTransportOpen(true);
      setCtxMenu(null);
      return;
    }
    try {
      await api.patch(`/units/${ctxUnit.id}/status/${statusId}`);
      addLog(`${ctxUnit.callsign} status changed`);
      loadUnitPositions();
    } catch { addLog('Error changing status'); }
    setCtxMenu(null);
  };

  const handleMapTransportConfirm = async () => {
    if (mapTransportUnit && mapTransportStatusId) {
      try {
        await api.patch(`/units/${mapTransportUnit.id}/status/${mapTransportStatusId}`, { destination: mapTransportDest });
        addLog(`${mapTransportUnit.callsign} transporting → ${mapTransportDest}`);
        loadUnitPositions();
      } catch { addLog('Error setting transport status'); }
    }
    setMapTransportOpen(false);
  };

  const handleCtxFieldIncident = () => {
    if (!ctxUnit) return;
    const lat = ctxUnit.location_lat;
    const lng = ctxUnit.location_lng;
    resetNewCallForm();
    setCallForm((f) => ({
      ...f,
      location_lat: lat,
      location_lng: lng,
      location_address: ctxUnit.current_location || `${lat}, ${lng}`,
      narrative: `Field incident generated by ${ctxUnit.callsign}`,
    }));
    if (lat && lng) geocodeCoords(lat, lng);
    setNewCallOpen(true);
    setCtxMenu(null);
  };

  const handleCreateCall = async () => {
    try {
      const res = await api.post('/calls', callForm);
      addLog(`New call created: ${res.data.cad_number} - ${res.data.call_type_code} at ${res.data.location_address}`);
      if (res.data.location_lat && res.data.location_lng && mapInstance.current) {
        L.marker([res.data.location_lat, res.data.location_lng])
          .addTo(mapInstance.current)
          .bindPopup(`<b>${escapeHtml(res.data.cad_number)}</b><br/>${escapeHtml(res.data.call_type_name)}<br/>${escapeHtml(res.data.location_address)}`)
          .openPopup();
      }
      setCreatedCall(res.data);
    } catch { addLog('Error creating call'); }
  };

  const handleAddNote = async () => {
    if (!createdCall || !newNote.trim()) return;
    try {
      await api.post(`/calls/${createdCall.id}/notes`, { note: newNote.trim() });
      setNewNote('');
      addLog(`Note added to ${createdCall.cad_number}`);
    } catch { /* ignore */ }
  };

  const handleCloseCallDialog = () => {
    setNewCallOpen(false);
    setCreatedCall(null);
    setNewNote('');
    resetNewCallForm();
    setCaseEntryOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 88px)' }}>
      <Box ref={mapRef} sx={{ flexGrow: 1, minHeight: 0 }} />

      <Paper sx={{ p: 1, bgcolor: '#1a1a2e' }}>
        <Box ref={commandLogRef} sx={{ maxHeight: 120, overflow: 'auto', mb: 1, fontFamily: 'monospace', fontSize: 13, color: '#4caf50', px: 1 }}>
          {commandLog.map((line, i) => (
            <Box key={i} sx={{ whiteSpace: 'pre-wrap' }}>{line}</Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth size="small" placeholder="Enter command (/help for commands)"
            value={command} onChange={(e) => setCommand(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { handleCommand(); }
              else if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (commandHistory.length === 0) return;
                const newIdx = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
                setHistoryIndex(newIdx);
                setCommand(commandHistory[newIdx]);
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (historyIndex === -1) return;
                const newIdx = historyIndex + 1;
                if (newIdx >= commandHistory.length) { setHistoryIndex(-1); setCommand(''); }
                else { setHistoryIndex(newIdx); setCommand(commandHistory[newIdx]); }
              }
            }}
            sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', bgcolor: '#0d0d1a', color: '#4caf50' } }}
          />
          <IconButton onClick={handleCommand} sx={{ color: '#4caf50' }}><Send /></IconButton>
          <Button variant="contained" startIcon={<Add />} onClick={() => setNewCallOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
            New Call
          </Button>
        </Box>
      </Paper>

      <Dialog open={newCallOpen} onClose={handleCloseCallDialog} maxWidth="md" fullWidth>
        <DialogTitle>{createdCall ? `Call ${createdCall.cad_number} — Add Notes / Case Entry` : 'New Call for Service'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Autocomplete
                freeSolo
                options={locationResults}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : `@${opt.name} — ${opt.address}`}
                inputValue={callForm.location_address}
                onInputChange={(_e, value) => {
                  setCallForm({ ...callForm, location_address: value });
                  if (value.startsWith('@') && value.length > 1) {
                    if (locationTimer.current) clearTimeout(locationTimer.current);
                    locationTimer.current = setTimeout(() => {
                      api.get(`/locations/search?q=${encodeURIComponent(value.slice(1))}`)
                        .then((r) => setLocationResults(r.data)).catch(() => {});
                    }, 300);
                  } else { setLocationResults([]); }
                }}
                onChange={(_e, val) => {
                  if (val && typeof val !== 'string') {
                    const fullAddr = [val.address, val.city, val.state, val.zip_code].filter(Boolean).join(', ');
                    let narrative = callForm.narrative;
                    if (val.hazard_info) narrative = `*** HAZARD: ${val.hazard_info} ***\n${narrative}`;
                    if (val.additional_notes) narrative = `${narrative}\nLocation Notes: ${val.additional_notes}`;
                    setCallForm({
                      ...callForm, location_address: fullAddr,
                      location_apt: val.apt_suite || callForm.location_apt,
                      location_lat: val.latitude, location_lng: val.longitude,
                      narrative: narrative.trim(),
                    });
                    setSelectedLocation(val);
                    if (val.latitude && val.longitude) geocodeCoords(val.latitude, val.longitude);
                  }
                }}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>@{opt.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{opt.address}{opt.city ? `, ${opt.city}` : ''}</Typography>
                      {opt.hazard_info && <Chip size="small" icon={<Warning />} label="Hazard" color="warning" sx={{ ml: 1 }} />}
                    </Box>
                  </li>
                )}
                onBlur={() => { if (callForm.location_address && !callForm.location_address.startsWith('@')) handlePreviewAddress(); }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Location / Address (type @ to search saved locations)" />
                )}
              />
              {selectedLocation?.hazard_info && (
                <Paper sx={{ mt: 1, p: 1, bgcolor: '#4a2800', border: '1px solid #ff9800' }}>
                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700 }}>
                    <Warning sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    HAZARD: {selectedLocation.hazard_info}
                  </Typography>
                </Paper>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Apt / Suite" value={callForm.location_apt} onChange={(e) => setCallForm({ ...callForm, location_apt: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Cross Street" value={callForm.cross_street} onChange={(e) => setCallForm({ ...callForm, cross_street: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Latitude" type="number" value={callForm.location_lat ?? ''} onChange={(e) => setCallForm({ ...callForm, location_lat: e.target.value ? Number(e.target.value) : null })} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Longitude" type="number" value={callForm.location_lng ?? ''} onChange={(e) => setCallForm({ ...callForm, location_lng: e.target.value ? Number(e.target.value) : null })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                startIcon={<Place />}
                variant="outlined"
                size="small"
                disabled={!callForm.location_address || previewLoading}
                onClick={handlePreviewAddress}
              >
                {previewLoading ? 'Geocoding...' : 'Preview Address'}
              </Button>
              {previewCoords && (
                <Button
                  startIcon={<Clear />}
                  variant="outlined"
                  size="small"
                  color="warning"
                  sx={{ ml: 1 }}
                  onClick={() => { resetPreviewMap(); setCallForm((f) => ({ ...f, location_lat: null, location_lng: null })); }}
                >
                  Clear Address
                </Button>
              )}
            </Grid>
            {previewCoords && (
              <Grid size={{ xs: 12 }}>
                <div ref={previewMapRef} style={{ height: 200, width: '100%', borderRadius: 4, border: '1px solid #555' }} />
                <Typography variant="caption" sx={{ mt: 0.5, color: '#aaa', display: 'block' }}>
                  Drag pin to fine-tune location
                </Typography>
                {previewAreaInfo && (
                  <Typography variant="body2" sx={{ mt: 0.5, color: '#90caf9', fontWeight: 600 }}>
                    📍 {previewAreaInfo}
                  </Typography>
                )}
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={callTypes}
                getOptionLabel={(opt) => `${opt.code} - ${opt.name}`}
                value={callTypes.find((ct) => ct.id === callForm.call_type_id) || null}
                onChange={(_, val) => {
                  setCallForm({ ...callForm, call_type_id: val?.id || 0, priority: val?.priority ?? callForm.priority });
                }}
                renderInput={(params) => <TextField {...params} label="Call Type" margin="normal" />}
                openOnFocus
                autoHighlight
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                options={callPriorities}
                getOptionLabel={(opt) => `${opt.code} - ${opt.name}`}
                value={callPriorities.find((cp) => cp.code === callForm.priority) || null}
                onChange={(_, val) => setCallForm({ ...callForm, priority: val?.code ?? 3 })}
                renderInput={(params) => <TextField {...params} label="Priority" margin="normal" />}
                openOnFocus
                autoHighlight
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                options={agencies}
                getOptionLabel={(opt) => opt.name}
                value={agencies.find((a) => a.id === callForm.agency_id) || null}
                onChange={(_, val) => setCallForm({ ...callForm, agency_id: val?.id || null })}
                renderInput={(params) => <TextField {...params} label="Agency" margin="normal" />}
                openOnFocus
                autoHighlight
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Reporting Party" value={callForm.reporting_party} onChange={(e) => setCallForm({ ...callForm, reporting_party: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Phone" value={callForm.reporting_phone} onChange={(e) => setCallForm({ ...callForm, reporting_phone: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField fullWidth multiline rows={3} label="Narrative" value={callForm.narrative} onChange={(e) => setCallForm({ ...callForm, narrative: e.target.value })} sx={{ flexGrow: 1 }} />
                <Tooltip title="Case Entry — guided interview">
                  <Button
                    variant="outlined" size="small" sx={{ mt: 1, minWidth: 'auto', px: 1 }}
                    onClick={() => setCaseEntryOpen(true)}
                    startIcon={<QuestionAnswer />}
                  >
                    Case Entry
                  </Button>
                </Tooltip>
              </Box>
            </Grid>
            {caseEntryOpen && (
              <Grid size={{ xs: 12 }}>
                <CaseEntryPanel
                  callTypeCode={callTypes.find(ct => ct.id === callForm.call_type_id)?.code}
                  onNarrativeUpdate={(text) => {
                    setCallForm((f: any) => ({ ...f, narrative: f.narrative ? f.narrative + '\n' + text : text }));
                  }}
                  onClose={() => setCaseEntryOpen(false)}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        {createdCall && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Add Notes</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth size="small" placeholder="Add a note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }} />
              <Button variant="contained" size="small" onClick={handleAddNote} disabled={!newNote.trim()} startIcon={<NoteAdd />}>Add</Button>
            </Box>
          </Box>
        )}
        <DialogActions>
          {createdCall ? (
            <Button variant="contained" onClick={handleCloseCallDialog}>Done</Button>
          ) : (
            <>
              <Button onClick={handleCloseCallDialog}>Cancel</Button>
              <Button variant="contained" onClick={handleCreateCall}>Create Call</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Menu
        open={!!ctxMenu}
        onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
      >
        {ctxUnit && (
          <MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 700 }}>
            {ctxUnit.callsign}
          </MenuItem>
        )}
        <Divider />
        <MenuItem disabled sx={{ fontSize: 12, opacity: '0.7 !important' }}>Change Status</MenuItem>
        {statusTypes.map((st) => (
          <MenuItem key={st.id} onClick={() => handleCtxStatusChange(st.id)}>
            <ListItemIcon><FiberManualRecord sx={{ color: st.color, fontSize: 14 }} /></ListItemIcon>
            <ListItemText>{st.code} — {st.name}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleCtxFieldIncident}>
          <ListItemIcon><NoteAdd fontSize="small" /></ListItemIcon>
          <ListItemText>Generate Field Incident</ListItemText>
        </MenuItem>
      </Menu>

      {/* Transport Destination Dialog */}
      <Dialog open={mapTransportOpen} onClose={() => setMapTransportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transport Destination — {mapTransportUnit?.callsign}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Select or enter the transport destination (hospital, facility, etc.)
          </Typography>
          <Autocomplete
            freeSolo
            options={mapDestinations.map((d) => d.name + (d.address ? ` (${d.address})` : ''))}
            value={mapTransportDest}
            onInputChange={(_, v) => setMapTransportDest(v)}
            renderInput={(params) => <TextField {...params} label="Destination" placeholder="e.g., Mercy Hospital, Cox South" autoFocus fullWidth />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapTransportOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMapTransportConfirm}>Set Transporting</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
